import { cn } from "cn";
import { CircleHelpIcon, ExternalLinkIcon } from "lucide-react";
import { NewTabLabel } from "#/components/Link/Link";
import {
	Popover,
	PopoverContent,
	type PopoverContentProps,
	PopoverTrigger,
} from "#/components/Popover/Popover";

type Icon = typeof CircleHelpIcon;

type Size = "small" | "medium";

export const HelpPopoverTrigger = PopoverTrigger;

const HelpPopoverIcon = CircleHelpIcon;

export const HelpPopover = Popover;

export const HelpPopoverContent: React.FC<PopoverContentProps> = ({
	className,
	...props
}) => {
	return (
		<PopoverContent
			side="bottom"
			align="start"
			collisionPadding={16}
			{...props}
			className={cn(
				"w-[320px] p-5 bg-surface-secondary border-surface-quaternary text-sm",
				className,
			)}
		/>
	);
};

type HelpPopoverIconTriggerProps = React.ComponentProps<"button"> & {
	size?: Size;
	hoverEffect?: boolean;
};

export const HelpPopoverIconTrigger: React.FC<HelpPopoverIconTriggerProps> = ({
	size = "medium",
	children = <HelpPopoverIcon />,
	hoverEffect = true,
	className,
	...buttonProps
}) => {
	return (
		<HelpPopoverTrigger asChild>
			<button
				aria-label="More info"
				{...buttonProps}
				type="button"
				className={cn(
					"flex size-6 items-center justify-center p-0",
					"border-0 border-none bg-transparent cursor-pointer text-inherit",
					size === "small" ? "[&_svg]:size-3" : "[&_svg]:size-4",
					hoverEffect &&
						"text-content-secondary hover:text-content-primary transition-colors",
					className,
				)}
			>
				{children}
			</button>
		</HelpPopoverTrigger>
	);
};

export const HelpPopoverTitle: React.FC<React.ComponentProps<"h4">> = ({
	children,
	className,
	...attrs
}) => {
	return (
		<h4
			className={cn(
				"mt-0 mb-2 text-content-primary text-sm leading-[150%] font-semibold",
				className,
			)}
			{...attrs}
		>
			{children}
		</h4>
	);
};

export const HelpPopoverText: React.FC<React.ComponentProps<"p">> = ({
	children,
	className,
	...attrs
}) => {
	return (
		<p
			className={cn(
				"my-1 text-sm text-content-secondary leading-normal",
				className,
			)}
			{...attrs}
		>
			{children}
		</p>
	);
};

type HelpPopoverLinkProps = {
	children?: React.ReactNode;
	href: string;
};

export const HelpPopoverLink: React.FC<HelpPopoverLinkProps> = ({
	children,
	href,
}) => {
	return (
		<a
			href={href}
			target="_blank"
			rel="noreferrer"
			className="flex items-center text-sm text-content-link no-underline hover:underline"
		>
			<ExternalLinkIcon className="size-3.5 mr-2" />
			{children}
			<NewTabLabel />
		</a>
	);
};

type HelpPopoverActionProps = {
	children?: React.ReactNode;
	icon: Icon;
	onClick: () => void;
	ariaLabel?: string;
};

export const HelpPopoverAction: React.FC<HelpPopoverActionProps> = ({
	children,
	icon: Icon,
	onClick,
	ariaLabel,
}) => {
	return (
		<button
			type="button"
			aria-label={ariaLabel ?? ""}
			className="flex items-center bg-transparent border-0 border-none text-content-link p-0 cursor-pointer text-sm"
			onClick={onClick}
		>
			<Icon className="size-3.5 mr-2" />
			{children}
		</button>
	);
};

export const HelpPopoverLinksGroup: React.FC<React.PropsWithChildren> = ({
	children,
}) => {
	return <div className="flex flex-col gap-2 mt-4">{children}</div>;
};
